import { useCallback, useMemo } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { workoutApi } from '@/api/workouts';
import { ApiError } from '@/api/client';
import type { CreateAnyWorkoutRequest, Exercise, Workout, WorkoutModality } from '@/types/Workout';

interface UseWorkoutsResult {
  workouts: Workout[];
  /** Server-side total for the modality, not just the loaded pages. */
  totalElements: number;
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  loadingMore: boolean;
  loadMore: () => void;
  refresh: () => Promise<void>;
  create: (data: CreateAnyWorkoutRequest) => Promise<{ ok: boolean; message?: string }>;
  remove: (id: string) => Promise<{ ok: boolean; message?: string }>;
}

/** Invalidation prefix — matches every per-modality workouts query. */
export const workoutsKey = ['workouts'] as const;
export const exerciseCatalogKey = ['exercise-catalog'] as const;

export const PAGE_SIZE = 50;

/** Shared exercise catalog for the strength picker. */
export function useExerciseCatalog(enabled: boolean): Exercise[] {
  const query = useQuery<Exercise[], Error>({
    queryKey: exerciseCatalogKey,
    queryFn: () => workoutApi.exercises().then((list) => list ?? []),
    enabled,
    staleTime: 60 * 60 * 1000, // catalog is static within a session
  });
  return query.data ?? [];
}

export function useWorkouts(enabled: boolean, modality?: WorkoutModality): UseWorkoutsResult {
  const qc = useQueryClient();
  const queryKey = [...workoutsKey, modality ?? 'ALL'];

  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => workoutApi.list(pageParam, PAGE_SIZE, modality),
    initialPageParam: 0,
    getNextPageParam: (last) => (last.hasNext ? last.page + 1 : undefined),
    enabled,
  });

  const workouts = useMemo(() => query.data?.pages.flatMap((p) => p.content) ?? [], [query.data]);
  const totalElements = query.data?.pages[0]?.totalElements ?? 0;

  const createMut = useMutation({
    mutationFn: (data: CreateAnyWorkoutRequest) => workoutApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: workoutsKey });
      qc.invalidateQueries({ queryKey: ['workout-stats'] });
    },
  });

  const removeMut = useMutation({
    mutationFn: (id: string) => workoutApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: workoutsKey });
      qc.invalidateQueries({ queryKey: ['workout-stats'] });
    },
  });

  const refresh = useCallback(async () => {
    await qc.invalidateQueries({ queryKey: workoutsKey });
  }, [qc]);

  const create = useCallback(
    async (data: CreateAnyWorkoutRequest) => {
      try {
        await createMut.mutateAsync(data);
        return { ok: true };
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to save workout';
        return { ok: false, message: msg };
      }
    },
    [createMut],
  );

  const remove = useCallback(
    async (id: string) => {
      try {
        await removeMut.mutateAsync(id);
        return { ok: true };
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to delete workout';
        return { ok: false, message: msg };
      }
    },
    [removeMut],
  );

  const loadMore = useCallback(() => {
    if (query.hasNextPage && !query.isFetchingNextPage) void query.fetchNextPage();
  }, [query]);

  const errIsAuth =
    query.error instanceof ApiError && (query.error.status === 401 || query.error.status === 403);
  const error = query.error && !errIsAuth ? query.error.message : null;

  return {
    workouts,
    totalElements,
    loading: query.isPending && enabled,
    error,
    hasMore: query.hasNextPage ?? false,
    loadingMore: query.isFetchingNextPage,
    loadMore,
    refresh,
    create,
    remove,
  };
}
